import { keys, omitBy } from 'lodash';
import Moment from 'moment';
import { stringify } from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import { subscriptionTypes, signalRHubManager } from './signalRHub/signalRHubManager';
import { dispatchToPageMap }
  from './../reducers/commonPageInfo';
import * as hubPayloadsHandlers from './signalRHub/hubPayloadsHandler';
import {
  build as buildConstants,
  mergeParamsWithUrlQuery,
  messaging as messagingHelpers,
  pageTypes,
  PROD_DASHBOARD_BASE_URL,
} from './../helpers';
import {
  buildIdsByJobId,
  buildsReducer,
  checkScenariosDispatcherIds,
  getAcquisitionsMetrics,
  getAcquisitionsSize,
  getArtifactHealthPlatformApplication,
  getArtifactHealthPlatforms,
  getBaselineBuildInfo,
  getBuildInfo,
  getCloudAcquisitions,
  getCloudBuildInfo,
  getDispatcherType,
  getJobById,
  getScenariosByDispatcherId,
  groupScenariosByDispatcherId,
  getLatestImageBaselines,
  getNameNumberJobIdPairs,
  jobBuildsReducer,
  routerExtraReducer
} from './../reducers';
import {
  api,
  apiActionDispatcher,
  apiCall,
  idMapWhiteToDictionary,
  multiIdApiActionDispatcher,
  multiIdApiCall,
  multiIdRequestCacheCheck,
  signalRWrappedApiCall
} from './api';
import * as actionItem from '../helpers/actionItem';

import * as annotations from './annotations';
import * as auth from './auth';
import * as builds from './builds';
import * as dispatchers from './dispatchers';
import * as installerFiles from './installerFiles';
import * as jira from './jira';
import * as jobRelationships from './jobRelationships';
import * as jobs from './jobs';
import * as metrics from './metrics';
import * as modal from './modal';
import * as nodeIssues from './nodeIssues';
import * as notifications from './notifications';
import * as params from './params';
import * as profile from './profile';
import * as sonar from './sonar';
import * as telemetry from './telemetry';
import * as toasts from './toasts';
import * as types from './types';

export {
  annotations,
  auth,
  builds,
  dispatchers,
  installerFiles,
  jira,
  jobRelationships,
  jobs,
  metrics,
  modal,
  nodeIssues,
  notifications,
  params,
  profile,
  sonar,
  telemetry,
  toasts,
  types
};
export { LOCATION_CHANGE } from 'react-router-redux';
export { toastr, push };

export const SET_DEVICE_NAME = 'SET_DEVICE_NAME';
export const setDevice = (deviceName) => ({
  type: SET_DEVICE_NAME,
  deviceName: deviceName.devicename
});

const fetchAcquisitionsMetricsDispatcher =
  apiActionDispatcher({
    actionTypes: types.acquisitionMetricsActionTypes,
    responseHandler: null
  });
export const fetchAcquisitionsMetrics = () => apiCall(
  fetchAcquisitionsMetricsDispatcher,
  api.acquisitionsPage.fetchAllMetrics,
  state => getAcquisitionsMetrics(state).length > 0,
  null,
  null
);

const fetchAcquisitionsSizeDispatcher =
  apiActionDispatcher({
    actionTypes: types.acquisitionSizeActionTypes,
    responseHandler: null
  });
export const fetchAcquisitionsSize = () => apiCall(
  fetchAcquisitionsSizeDispatcher,
  api.acquisitionsPage.fetchAllSizes,
  state => getAcquisitionsSize(state).length > 0,
  null,
  null
);

const fetchCloudAcquisitionsDispatcher =
  apiActionDispatcher({ actionTypes: types.cloudAcquisitionActionTypes });
export const fetchCloudAcquisitions = () => apiCall(
  fetchCloudAcquisitionsDispatcher,
  api.cloudAcquisitions.fetchRecent,
  state => getCloudAcquisitions(state).length > 0,
  null,
  state => state.cloudAcquisitions.request
);

const fetchCloudAutomationBuildInfoDispatcher =
  apiActionDispatcher({ actionTypes: types.cloudAutomationBuildInfoActionTypes });
export const fetchBuildInfo = () => apiCall(
  fetchCloudAutomationBuildInfoDispatcher,
  api.cloudAcquisitions.fetchLatestSuccesfulBuildInfo,
  state => getCloudBuildInfo(state).length > 0,
  null,
  state => state.cloudAcquisitionBuildInfo.request
);

const searchExceptionsByTermDispatcher =
  apiActionDispatcher({
    actionTypes: types.searchExceptionsByTermActionTypes,
    responseHandler: response => response
  });
export const getExceptionsBySearch =
  (dateSelected, deviceType, imageType, searchTerm) => {
    return apiCall(
      searchExceptionsByTermDispatcher,
      api.acquireTelemetry.searchExceptionsByTerm,
      null,
      null,
      null,
      [dateSelected, deviceType, imageType, searchTerm]);
  };

export const SET_SEARCHTERM = 'SET_SEARCHTERM';
export const setTelemetrySearchTerm = (searchTerm) => ({
  type: SET_SEARCHTERM,
  searchTerm: searchTerm
});

export const SET_DEVICETYPE = 'SET_DEVICETYPE';
export const setTelemetryDeviceType = (deviceType) => ({
  type: SET_DEVICETYPE,
  deviceType: deviceType
});

export const SET_IMAGETYPE = 'SET_IMAGETYPE';
export const setTelemetryImageType = (imageType) => ({
  type: SET_IMAGETYPE,
  imageType: imageType
});

export const SET_DATE = 'SET_DATE';
export const setTelemetryDate = (date) => ({
  type: SET_DATE,
  date: date
});

const fetchScenariosDispatcher =
  () => apiActionDispatcher({ actionTypes: types.scenariosActionTypes });
export const fetchScenariosByDispatcherIds = (dispatcherIds) => {
  const dispatcher = fetchScenariosDispatcher();
  return signalRWrappedApiCall(
    apiCall(
      dispatcher,
      api.scenarios.fetchByDispatcherIds,
      state => checkScenariosDispatcherIds(state, dispatcherIds),
      null,
      state => multiIdRequestCacheCheck(state.scenarios.request, dispatcherIds),
      dispatcherIds
    ),
    dispatcher,
    signalRHubManager,
    subscriptionTypes.scenarios,
    state => keys(groupScenariosByDispatcherId(state)),
    (args, payloads) =>
      hubPayloadsHandlers.payloadsByIdHandler(args, payloads, 'dispatcher')
  );
};

export const getNameNumberIdPairsFromDispatch = () => (dispatch, getState) => {
  const state = getState();
  return getNameNumberJobIdPairs(state);
};

const fetchLatestImageBaselinesDispatcher = apiActionDispatcher({
  actionTypes: types.latestImageBaselinesActionTypes,
  responseHandler: response => response
});
export const fetchLatestImageBaselines = () => signalRWrappedApiCall(
  apiCall(
    fetchLatestImageBaselinesDispatcher,
    api.jobBuilds.fetchLatestImageBaselines,
    state => getLatestImageBaselines(state).length > 0,
    null
  ),
  fetchLatestImageBaselinesDispatcher,
  signalRHubManager,
  subscriptionTypes.latestImageBaselines
);

const fetchFailedTestArtifactsDispatcher = multiIdApiActionDispatcher({
  actionTypes: types.failedTestArtifactsActionTypes,
  accumulatorIdProp: 'job_id',
  responseHandler: byId => idMapWhiteToDictionary(byId)
});
export const fetchFailedTestArtifactsByJobIds = (jobIds) => multiIdApiCall(
  fetchFailedTestArtifactsDispatcher,
  api.white.fetchFailedTestArtifactsByJobIds,
  (state, ids) => multiIdRequestCacheCheck(state.failedTestArtifacts.request, ids),
  jobIds
);

const editJobLabelDispatcher = apiActionDispatcher({
  actionTypes: types.jobLabelEditActionTypes,
  responseHandler: response => response
});
export const addJobLabel = (jobId, label) => apiCall(
  editJobLabelDispatcher,
  api.jobLabels.addByJobId,
  null,
  null,
  null,
  [jobId, label]
);
export const removeJobLabel = (jobId, label) => apiCall(
  editJobLabelDispatcher,
  api.jobLabels.removeByJobId,
  null,
  null,
  null,
  [jobId, label]
);

export const jumpToScenario = (dispatcherNameNumber, pageType) => (dispatch) => {
  if (dispatcherNameNumber) {
    const number = dispatcherNameNumber.split('_').pop();
    const name = dispatcherNameNumber.slice(0, dispatcherNameNumber.lastIndexOf('_'));

    dispatch(push(`${dispatchToPageMap[pageType.type]}?nb=${number}-&nm=${name}`));
  }
};

export const HIGHLIGHT_BAR = 'HIGHLIGHT_BAR';
export const highlightBar = (chart) => {
  if (!chart._model || !chart._model.label) {
    return;
  }
  return {
    type: HIGHLIGHT_BAR,
    image: chart._model.label
  };
};

export const SET_METRICS_EXPANDED = 'SET_METRICS_EXPANDED';
export const setMetricsExpanded = (expanded) => ({
  type: SET_METRICS_EXPANDED,
  expanded
});

export const SET_LATEST_METRICS_ONLY = 'SET_LATEST_METRICS_ONLY';
export const setLatestMetricsOnly = (latestMetricsOnly) => ({
  type: SET_LATEST_METRICS_ONLY,
  latestMetricsOnly
});

export const SET_SHOW_SOURCES = 'SET_SHOW_SOURCES';
export const setShowSources = (showSources) => ({
  type: SET_SHOW_SOURCES,
  showSources
});

export const SET_CONCERNING_METRICS_ONLY = 'SET_CONCERNING_METRICS_ONLY';
export const setConcerningMetricsOnly = (concerningMetricsOnly) => ({
  type: SET_CONCERNING_METRICS_ONLY,
  concerningMetricsOnly
});

export const SET_CONCERNING_TRENDING_ONLY = 'SET_CONCERNING_TRENDING_ONLY';
export const setConcerningTrendingOnly = (concerningTrendingOnly) => ({
  type: SET_CONCERNING_TRENDING_ONLY,
  concerningTrendingOnly
});

export const SET_SELECTED_TESTS_ONLY = 'SET_SELECTED_TESTS_ONLY';
export const setCurrentDispatcherTestsOnly = (currentDispatcherTestsOnly) => ({
  type: SET_SELECTED_TESTS_ONLY,
  currentDispatcherTestsOnly
});

export const setBuildInfo = (info, isBaseline) => (dispatch, getState) => {
  const state = getState();
  const buildInfo = !isBaseline ? info : getBuildInfo(state);
  const baselineBuildInfo = isBaseline ? info : getBaselineBuildInfo(state);
  const translateBuildType = bt => bt === buildConstants.LATEST_RELEASE ? 'release' : bt;

  const params = omitBy({
    t: translateBuildType(buildInfo.buildType),
    n: buildInfo.buildNumber,
    p: buildInfo.product,
    bt: translateBuildType(baselineBuildInfo.buildType),
    bn: baselineBuildInfo.buildNumber,
    bp: baselineBuildInfo.product
  }, p => !p || p.length === 0);

  dispatch(push(`/build?${stringify(params)}`));
};

export const setBuildHealthPageInfo = (buildHealthPageInfo) => (dispatch) => {
  const params = omitBy({
    n: buildHealthPageInfo.buildNumber,
    i: buildHealthPageInfo.buildIds,
    p: buildHealthPageInfo.product,
    e: buildHealthPageInfo.extended
  }, p => !p || p.length === 0);

  dispatch(push(`/build-health?${stringify(params)}`));
};

export const SET_METRICS_SORTER = 'SET_METRICS_SORTER';
export const setMetricsSorter = (sorter) => ({
  type: SET_METRICS_SORTER,
  sorter
});

export const removeOutlierClick = (jobId) =>
  (dispatch) => {
    dispatch(
      removeJobLabel(jobId, 'Outlier')
    );
    toastr.success('Outlier label removed.');
  };

export const addOutlierClick = (jobId) =>
  (dispatch) => {
    dispatch(
      addJobLabel(jobId, 'Outlier')
    );
    toastr.success('Outlier label added.');
  };

// Unused???
export const CHANGE_COMMENT_MODAL_STATUS =
  'CHANGE_COMMENT_MODAL_STATUS';
export const changeCommentModalStatus = (status) => {
  return {
    type: CHANGE_COMMENT_MODAL_STATUS,
    status
  };
};

export const confirmRetriggerScenarios = (scenarios) => (dispatch) => {
  for (let i = 0; i < scenarios.length; i++) {
    const runner = scenarios[i].runner;
    dispatch(actionItem._startJob(runner));
  }
};

const verifyArtifactApplicationDispatcher =
  apiActionDispatcher({
    actionTypes: types.verifyArtifactApplicationActionTypes,
    responseHandler: (response) => {
      if (response) {
        toastr.success('Application Verification', 'Operation was successful');
      }
      else {
        toastr.error('Application Verification', 'An error has occurred');
      }
    }
  });

export const confirmVerifyArtifactApplication = (applicationInfo) =>
  apiCall(
    verifyArtifactApplicationDispatcher,
    api.artifactHealth.verifyArtifactApplication,
    null,
    null,
    null,
    [applicationInfo],
  );

export const SHOW_CASE_AMENDING_BY_IMAGE = 'SHOW_CASE_AMENDING_BY_IMAGE';
export const showCaseAmendingByImage = (visible) => ({
  type: SHOW_CASE_AMENDING_BY_IMAGE,
  visible
});

export const TOGGLE_DETAILS = 'TOGGLE_DETAILS';
export const toggleDetails = (item) => ({
  type: TOGGLE_DETAILS,
  item
});

export const EXPAND_ALL_DETAILS = 'EXPAND_ALL_DETAILS';
export const expandAllDetails = (order, scenariosCount) => ({
  type: EXPAND_ALL_DETAILS,
  order,
  scenariosCount
});

export const COLLAPSE_ALL_DETAILS = 'COLLAPSE_ALL_DETAILS';
export const collapseAllDetails = (order, scenariosCount) => ({
  type: COLLAPSE_ALL_DETAILS,
  order,
  scenariosCount
});

export const TOGGLE_TICK = 'TOGGLE_TICK';
export const toggleTick = (item) => ({
  type: TOGGLE_TICK,
  item
});

export const TOGGLE_PANEL = 'TOGGLE_PANEL';
export const togglePanel = (item) => ({
  type: TOGGLE_PANEL,
  item
});

export const SET_PAGE = 'SET_PAGE';
export const setPage = (page) => ({
  type: SET_PAGE,
  page
});

export const setDispatcherJobInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const dispatcherType = getDispatcherType(state);

  const concerningParams = omitBy({
    nm: info.jobName,
    nb: info.jobNumber,
    nj: info.numJobs
  }, p => !p || p.length === 0);

  const otherParams = routerExtraReducer
    .sortQueryParamsByConcerning(state, Object.keys(concerningParams))
    .other;

  const params = {
    ...otherParams,
    ...concerningParams
  };

  switch (dispatcherType) {
    case pageTypes.CASEAMENDINGTESTS.type: {
      return dispatch(push(`/case-amending?${stringify(params)}`));
    }
    case pageTypes.ARTIFACTREGRESSION.type: {
      return dispatch(push(`/artifact-regression?${stringify(params)}`));
    }
    case pageTypes.EXPORTTESTS.type: {
      return dispatch(push(`/export-regression?${stringify(params)}`));
    }
    case pageTypes.REMOTEACQUIREREGRESSION.type: {
      return dispatch(push(`/remote-acquire-regression?${stringify(params)}`));
    }
    case pageTypes.DIFFTOOLREGRESSION.type: {
      return dispatch(push(`/diff?${stringify(params)}`));
    }
    case pageTypes.ACQUISITIONTESTS.type: {
      return dispatch(push(`/acquisition?${stringify(params)}`));
    }
    case pageTypes.UITESTS.type: {
      return dispatch(push(`/white?${stringify(params)}`));
    }
    case pageTypes.INSTALLERTESTS.type: {
      return dispatch(push(`/installer?${stringify(params)}`));
    }
    case pageTypes.CASEMERGINGTESTS.type: {
      return dispatch(push(`/case-merging?${stringify(params)}`));
    }
    case pageTypes.TESTSTUDIO.type: {
      return dispatch(push(`/test-studio?${stringify(params)}`));
    }
    case pageTypes.AXIOMUITESTS.type: {
      return dispatch(push(`/axiom-ui?${stringify(params)}`));
    }
    default:
      return null;
  }
};

export const setActiveTab = (key) => (dispatch, getState) => {
  const state = getState();
  const concerningParams = omitBy({ tab: key }, p => !p || p.length === 0) ;

  const otherParams = routerExtraReducer
    .sortQueryParamsByConcerning(state, Object.keys(concerningParams))
    .other;

  const params = {
    ...otherParams,
    ...concerningParams
  };

  return dispatch(push(`${routerExtraReducer.getPathName(state)}?${stringify(params)}`));
};

export const setAwsPageInfo = (info) => (dispatch) => {
  const params = omitBy({
    a: info.account,
    r: info.region
  }, p => !p || p.length === 0);

  dispatch(push(`/aws-instances-cost?${stringify(params)}`));
};

export const setPerformancePageType = (info) => (dispatch) => {
  switch (info.performanceType) {
    case pageTypes.CASEAMENDINGTESTS.type: {
      return dispatch(push('/case-amending-performance'));
    }
    case pageTypes.CASEAMENDINGKPPTESTS.type: {
      return dispatch(push('/case-amending-kpp-performance'));
    }
    case pageTypes.EXPORTTESTS.type: {
      return dispatch(push('/export-performance'));
    }
    case pageTypes.PERFORMANCEMETRICS.type: {
      return dispatch(push('/performance-metrics'));
    }
    case pageTypes.JOBBENCHMARK.type: {
      return dispatch(push('/job-benchmark-performance'));
    }
    default:
      return dispatch(push('/performance'));
  }
};

const updateCaseAmendingBaselineDispatcher = apiActionDispatcher({
  actionTypes: types.updateCaseAmendingBaselineActionTypes,
  responseHandler: response => response
});

export const updateCaseAmendingBaseline = (imageName, jobType, jobNumber) => {
  return apiCall(
    updateCaseAmendingBaselineDispatcher,
    api.caseAmending.updateBaseline,
    null,
    null,
    null,
    [imageName, jobType, jobNumber]
  );
};

export const SET_SEARCH_TERM =
  'SET_SEARCH_TERM';
export const setSearchTerm = (text) => {
  return {
    type: SET_SEARCH_TERM,
    text: text
  };
};

const triggerJobDispatcher = apiActionDispatcher({
  actionTypes: types.triggerJobActionTypes,
  responseHandler: null,
  successMessage: messagingHelpers.RETRIGGER_SUCCESS,
  useResponseErrorMessage: true,
  errorExtendedTimeout: 10000
});
export const triggerJobFromId = (id) => {
  return apiCall(
    triggerJobDispatcher,
    api.jenkins.triggerJobFromId,
    null,
    null,
    null,
    [id]
  );
};
export const triggerWhatsIn = (oldVersion, newVersion, product) => {
  return apiCall(
    triggerJobDispatcher,
    api.jenkins.triggerWhatsIn,
    null,
    null,
    null,
    [oldVersion, newVersion, product]
  );
};

export const triggerBaselineUpdate = (platform, application, jobNumber) => {
  return apiCall(
    triggerJobDispatcher,
    api.jenkins.triggerBaselineUpdate,
    null,
    null,
    null,
    [platform, encodeURIComponent(application), jobNumber]
  );
};

const toggleJenkinsBuildForeverDispatcher = apiActionDispatcher({
  actionTypes: types.toggleJenkinsBuildForeverActionTypes,
  responseHandler: response => response,
  useResponseErrorMessage: true,
  errorExtendedTimeout: 10000
});
export const toggleJenkinsBuildForever = (jobName, server, jobNumber) => {
  return apiCall(
    toggleJenkinsBuildForeverDispatcher,
    api.jenkins.toggleJenkinsBuildForever,
    null,
    null,
    null,
    [jobName, server, jobNumber]
  );
};

export const SET_SCENARIO_KEEP_FOREVER =
  'SET_SCENARIO_KEEP_FOREVER';
export const setScenarioKeepForever = (title, keep) => {
  return {
    type: SET_SCENARIO_KEEP_FOREVER,
    title: title,
    keep: keep
  };
};

export const CHANGE_USERNAME =
  'CHANGE_USERNAME';
export const changeUserName = (error, username) => {
  return {
    type: CHANGE_USERNAME,
    error,
    username
  };
};

export const CHANGE_TICKET =
  'CHANGE_TICKET';
export const changeTicket = (error, ticket) => {
  return {
    type: CHANGE_TICKET,
    error,
    ticket
  };
};

export const CHANGE_COMMENT =
  'CHANGE_COMMENT';
export const changeComment = (comment) => {
  return {
    type: CHANGE_COMMENT,
    comment
  };
};

const updateJiraIssueDescriptionDispatcher =
  apiActionDispatcher({
    actionTypes: types.updateJiraIssueDescriptionActionTypes,
    responseHandler: null,
    errorMessage: 'Could not login to JIRA'
  });
export const updateJiraIssueDescription =
  (commentInfo, position, authToken, routerPath, activePage) => {
    const { item, ticket, comment, userName } = commentInfo;
    if (routerPath.includes('&ex'))
      routerPath = routerPath.substring(0, routerPath.indexOf('&ex'));

    const userComment = comment ?
      ` with the comment: {quote}${comment}\n??${userName}` :
      '\n';
    const jiraComment =
      `Ticket was linked to ${item.name} on dispatcher \
      ${PROD_DASHBOARD_BASE_URL}${routerPath}&ex=${position}\
      &pg=${activePage}${userComment}`;

    return apiCall(
      updateJiraIssueDescriptionDispatcher,
      api.jira.updateDescription,
      null,
      null,
      null,
      [ticket, jiraComment, authToken]
    );
  };

export const SET_COMMENT_MODAL_ERROR =
  'SET_COMMENT_MODAL_ERROR';
export const setCommentModalError = (error) => {
  return {
    type: SET_COMMENT_MODAL_ERROR,
    error
  };
};

export const SET_COMMENT_MODAL_SUBMITTING =
  'SET_COMMENT_MODAL_SUBMITTING';
export const setCommentModalSubmitting = (submitting) => {
  return {
    type: SET_COMMENT_MODAL_SUBMITTING,
    submitting
  };
};

export const SELECT_JOB = 'SELECT_JOB';
export const selectJob = (chart) => {
  const footer = chart._chart.tooltip._model.footer[0];
  return {
    type: SELECT_JOB,
    value: chart._chart.config.data.labels[chart._index],
    jobId: footer.split(' ')[1],
    index: chart._index
  };
};

export const SELECT_REASON = 'SELECT_REASON';
export const selectReason = (chart) => {
  return {
    type: SELECT_REASON,
    value: chart._chart.config.data.labels[chart._index]
  };
};

export const SELECT_PERFORMANCE_JOB = 'SELECT_PERFORMANCE_JOB';
export const selectPerformanceJob = (chart) => {
  const index = chart._index;
  const footer = chart._chart.tooltip._model.footer[0];
  return {
    type: SELECT_PERFORMANCE_JOB,
    value: chart._chart.config.data.labels[index],
    jobId: footer.split(' ')[1],
    index
  };
};

export const RESET_PERFORMANCE_PAGE = 'RESET_PERFORMANCE_PAGE';
export const resetPerformancePage = () => {
  return { type: RESET_PERFORMANCE_PAGE };
};

export const setMetricsByDateInfo = (info, route) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const params = {
    ts: info.timestamp,
    c: info.runCount,
  };
  const merged = mergeParamsWithUrlQuery(search, params);
  const path = route ? route : routerExtraReducer.getPathName(state);
  dispatch(push(`${path}?${stringify(merged)}`));
};

export const setReleaseMetricsInfo = (info) => (dispatch) => {
  const params = {
    p: info.product,
    c: info.case,
    s: info.scenario,
    v: info.sinceVersion
  };
  dispatch(push(`/release-metrics?${stringify(params)}`));
};

export const setCaseMetricsInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const scenarios = info ? getScenariosByDispatcherId(state, info.dispatcherId) : [];
  const selectedScenarios = scenarios ?
    scenarios.filter(s => s.name.toLowerCase() === info.image) : [];
  const selectedScenario = !selectedScenarios.length ? undefined : selectedScenarios[0];
  const selectedJob = selectedScenario ?
    getJobById(state, selectedScenario.runner) : undefined;
  const params = !info ?
    {} :
    {
      ts: info.timestamp,
      c: info.runCount,
      t: info.toggles,
      f: info.buildFilter,
      n: info.nodes,
      i: info.image,
      jn: info.givenJob ? info.givenJob : selectedJob ? selectedJob.number : undefined,
      ji: selectedJob ? selectedJob.id : undefined,
      d: info.dispatcherId,
    };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/case-metrics?${stringify(merged)}`));
};

export const setEngineMetricsInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const scenarios = info ? getScenariosByDispatcherId(state, info.dispatcherId) : [];
  const selectedScenarios = scenarios ?
    scenarios.filter(s => s.name.toLowerCase() === info.image) : [];
  const selectedScenario = !selectedScenarios.length ? undefined : selectedScenarios[0];
  const selectedJob = selectedScenario ?
    getJobById(state, selectedScenario.runner) : undefined;
  const params = !info ?
    {} :
    {
      ts: info.timestamp,
      c: info.runCount,
      t: info.toggles,
      f: info.buildFilter,
      n: info.nodes,
      i: info.image,
      jn: info.givenJob ? info.givenJob : selectedJob ? selectedJob.number : undefined,
      ji: selectedJob ? selectedJob.id : undefined,
      d: info.dispatcherId,
    };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/engine-metrics?${stringify(merged)}`));
};

export const setArtifactsMetricsInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const params = !info ?
    {} :
    {
      ts: info.timestamp,
      c: info.runCount,
      t: info.toggles,
      f: info.buildFilter,
      n: info.nodes,
      i: info.image,
      j: info.givenJob,
      d: info.dispatcherId,
    };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/artifacts-metrics?${stringify(merged)}`));
};

export const setPerformanceMetricsInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const params = {
    t: info.toggles,
    f: info.buildFilter,
    n: info.nodes,
    c: info.caseName,
    a: info.action,
    j: info.givenJob,
    d: info.dispatcherId,
  };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/performance-metrics?${stringify(merged)}`));
};

export const setJobBenchmarkMetricsInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const params = {
    f: info.buildFilter,
    n: info.nodes,
    c: info.caseName,
    a: info.action,
    b: info.snPartI,
    i: info.snPartII,
    j: info.givenJob,
    d: info.dispatcherId,
  };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/job-benchmark-performance?${stringify(merged)}`));
};

export const setExportPerformanceInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const params = {
    t: info.toggles,
    f: info.buildFilter,
    n: info.nodes,
    e: info.snPartI,
    g: info.snPartII,
    i: info.snPartIII,
    j: info.givenJob,
    d: info.dispatcherId,
  };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/export-performance?${stringify(merged)}`));
};

export const setCaseAmendingPerformanceInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const params = {
    t: info.toggles,
    f: info.buildFilter,
    b: info.snPartI,
    i: info.snPartII,
    j: info.givenJob,
    d: info.dispatcherId,
  };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/case-amending-performance?${stringify(merged)}`));
};

export const setCaseAmendingKppPerformanceInfo = (info) => (dispatch, getState) => {
  const state = getState();
  const search = routerExtraReducer.getSearch(state);
  const params = {
    t: info.toggles,
    f: info.buildFilter,
    i: info.snPartII,
    j: info.givenJob,
    d: info.dispatcherId,
  };
  const merged = mergeParamsWithUrlQuery(search, params);
  dispatch(push(`/case-amending-kpp-performance?${stringify(merged)}`));
};

export const SET_ARTIFACTS_INFO = 'SET_ARTIFACTS_INFO';
export const setArtifactsInfo = (info) => {
  return {
    type: SET_ARTIFACTS_INFO,
    info
  };
};

export const REGRESSION_PAGE = 'REGRESSION_PAGE';
export const changeScenariosBy = (scenariosBy) => {
  return {
    type: REGRESSION_PAGE,
    scenariosBy: scenariosBy
  };
};

export const SET_SHOW_BUILD_COMPLEMENT = 'SET_SHOW_BUILD_COMPLEMENT';
export const setShowBuildComplement = (show) => ({
  type: SET_SHOW_BUILD_COMPLEMENT,
  show
});

const fetchJobBuildsDispatcher = apiActionDispatcher({
  actionTypes: types.jobBuildsActionTypes,
  responseHandler: response => response
});
export const fetchJobBuilds = (days) => signalRWrappedApiCall(
  apiCall(
    fetchJobBuildsDispatcher,
    api.jobBuilds.fetchRecent,
    state => {
      // * this is expected to be called in conjunction with fetchBuilds
      // * and with the same 'day' param to be considered relevant.
      // As such the logic below dependents on values returned from fetchBuilds
      // fetchBuilds will have had to be finished for this to evaluate properly
      const jobBuildIds = jobBuildsReducer.jobBuildIds(state);

      const builds = buildsReducer.buildsById(state);
      const buildIds = keys(builds) || [];

      if (jobBuildIds.length === 0 || buildIds.length === 0) {
        return false;
      }

      //id of earliest build fetched by fetchBuilds
      const earliestBuildId = buildIds[0];
      //if of earliest build present in fetched jobs
      const earliestJobBuildId = jobBuildIds[0];

      if (earliestJobBuildId < parseInt(earliestBuildId)) {
        //a job has a build older than the oldest fetched build
        return true;
      }

      const givenDay = Moment().startOf('day').subtract(days, 'days');

      const build = buildsReducer.getBuildById(state, earliestJobBuildId);
      if (build && givenDay.diff(Moment(build.updated_at), 'days') >= 0) {
        return true;
      }
      return false;
    },
    null,
    state => state.jobBuilds.request,
    null,
    [days]
  ),
  fetchJobBuildsDispatcher,
  signalRHubManager,
  subscriptionTypes.jobBuilds,
  state => keys(buildIdsByJobId(state)),
  (args, payloads) =>
    hubPayloadsHandlers.payloadsToKeyValuesHandler(args, payloads, 'job_id', 'build_id')
);
export const fetchJobBuildsByBuildIds = (buildIds) => multiIdApiCall(
  () => fetchJobBuildsDispatcher,
  api.jobBuilds.fetchByBuildIds,
  (state, ids) => multiIdRequestCacheCheck(state.jobBuilds.byId, ids),
  buildIds
);

export const fetchJobBuildsByJobIds = (jobIds) => multiIdApiCall(
  () => fetchJobBuildsDispatcher,
  api.jobBuilds.fetchByJobIds,
  (state, ids) => multiIdRequestCacheCheck(state.jobBuilds.byId, ids),
  jobIds
);

const fetchJobLabelsDispatcher =
  multiIdApiActionDispatcher({ actionTypes: types.jobLabelsActionTypes });
export const fetchJobLabels = (jobIds) => signalRWrappedApiCall(
  multiIdApiCall(
    fetchJobLabelsDispatcher,
    api.jobLabels.fetchByJobIds,
    (state, ids) => multiIdRequestCacheCheck(state.jobLabels.byId, ids),
    jobIds
  ),
  fetchJobLabelsDispatcher([]),
  signalRHubManager,
  subscriptionTypes.jobLabels,
  state => state.jobLabels.request.args,
  (args, payloads) =>
    hubPayloadsHandlers.payloadsToKeyValuesHandler(args, payloads, 'job_id', 'label')
);

const fetchArtifactHealthPlatformStatusesDispatcher =
  apiActionDispatcher({
    actionTypes: types.fetchArtifactHealthPlatformStatusesTypes,
    responseHandler: null
  });
export const fetchArtifactHealthPlatformStatuses = () => apiCall(
  fetchArtifactHealthPlatformStatusesDispatcher,
  api.artifactHealth.fetchPlatformStatuses,
  state => getArtifactHealthPlatforms(state).length > 0,
  null,
  null
);

const fetchArtifactHealthApplicationStatusesDispatcher =
  apiActionDispatcher({
    actionTypes: types.fetchArtifactHealthApplicationStatusesTypes,
    responseHandler: null
  });
export const fetchArtifactHealthApplicationStatuses = (id, platform, application) =>
  apiCall(
    fetchArtifactHealthApplicationStatusesDispatcher,
    api.artifactHealth.fetchApplicationStatus,
    state =>
      getArtifactHealthPlatformApplication(state, platform, application) !== undefined,
    null,
    null,
    [id]
  );

const fetchAWSCostReportDispatcher = apiActionDispatcher({
  actionTypes: types.fetchAWSCostReportActionTypes,
  responseHandler: response => response
});
export const fetchAWSCostReport = (periodicity, startDate, endDate) =>
  apiCall(
    fetchAWSCostReportDispatcher,
    api.aws.fetchCostReport,
    null,
    null,
    null,
    [periodicity, startDate, endDate]
  );

export const CHANGE_ARTIFACT_HEALTH_FILTER = 'CHANGE_ARTIFACT_HEALTH_FILTER';
export const changeArtifactHealthFilter = (filter, checked) => ({
  type: CHANGE_ARTIFACT_HEALTH_FILTER,
  filter,
  checked
});

export const CHANGE_ARTIFACT_HEALTH_ORDER = 'CHANGE_ARTIFACT_HEALTH_ORDER';
export const changeArtifactHealthOrder = (orderBy) => ({
  type: CHANGE_ARTIFACT_HEALTH_ORDER,
  orderBy
});

export const CHANGE_REGRESSION_FILTER = 'CHANGE_REGRESSION_FILTER';
export const changeRegressionFilter = (filter, checked) => ({
  type: CHANGE_REGRESSION_FILTER,
  filter,
  checked
});

export const SET_CONNECTION_STATUS = 'SET_CONNECTION_STATUS';
export const setConnectionStatus = (isConnected) => {
  return ({
    type: SET_CONNECTION_STATUS,
    connected: isConnected
  });
};

export const SET_AWS_SYNC_DATETIME = 'SET_AWS_SYNC_DATETIME';
export const setAwsDataSyncDatetime = (datetime) => {
  return {
    type: SET_AWS_SYNC_DATETIME,
    datetime
  };
};

export const SORT_AWS_COST_TABLE = 'SORT_AWS_TABLE';
export const sortAWSCostTable = (sortingKey) => {
  return {
    type: SORT_AWS_COST_TABLE,
    sortingKey
  };
};


// WEBPACK FOOTER //
// src/actions/index.js